import qs from 'qs';
import { captureException, setContext } from '@sentry/react';
import { useNetworkErrorStore } from '@lib';
import { RestApiError } from '../utils';
import { AuthUtils, LocalStorageUtils } from '@shared/utils';
import { HttpMethodEnum, type IRequest, type IRequestParams } from '../models';

export class Api {
  private static readonly baseUrl = process.env.REACT_APP_API_PROXY!;

  private static setNetworkError = (value: boolean) => useNetworkErrorStore.setState({ networkError: value });

  private static _objectToQuery = (obj?: object) => {
    if (!obj) return '';

    return qs.stringify(obj, {
      addQueryPrefix: true,
      indices: false,
      allowDots: true,
      skipNulls: true,
    });
  };

  protected static get<Response>(request: IRequestParams<null>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<void, Response>({
        method: HttpMethodEnum.Get,
        url: request.url + this._objectToQuery(request.queryParams),
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static post<Request, Response = unknown>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Post,
        url: request.url + this._objectToQuery(request.queryParams),
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static put<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Put,
        url: request.url + this._objectToQuery(request.queryParams),
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static patch<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Patch,
        url: request.url + this._objectToQuery(request.queryParams),
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  protected static delete<Request, Response = unknown>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethodEnum.Delete,
        url: request.url + this._objectToQuery(request.queryParams),
        data: request.data,
        headers: request.headers,
        signal: request.signal,
      }),
    );
  }

  private static async _wrapRequest<Response>(handler: () => Promise<Response>) {
    try {
      this.setNetworkError(false);
      return await handler();
    } catch (error) {
      if (error instanceof TypeError) this.setNetworkError(true);
      throw error;
    }
  }

  private static async _request<Request, Response>(request: IRequest<Request>): Promise<Response> {
    const requestId = crypto.randomUUID();

    const headers: Record<string, string> =
      request?.data instanceof FormData
        ? { ...request.headers }
        : {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Request-Id': requestId,
            ...request.headers,
          };
    if (AuthUtils.authDataCached?.accessToken) {
      headers.Authorization = `Bearer ${AuthUtils.authDataCached.accessToken}`;
      headers.partnerId = (LocalStorageUtils.getSelectedPartnerId() || 1).toString();
    }

    const options = {
      method: request.method || 'GET',
      headers,
      body: request.data && request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
      signal: request.signal,
    };

    const response = await fetch(this.baseUrl + request.url, options);

    if (response.ok) {
      const contentType = response.headers.get('content-type');

      if (contentType?.indexOf('application/json') !== -1) return response.json();

      return {
        data: await response.blob(),
        fileName: response.headers.get('content-disposition'),
      } as Response;
    } else {
      let error;
      let message = '';

      if (response.status === 401) {
        message = 'Unauthorized';
        error = new RestApiError(message, response.status, {}, request.method, request.url);
      } else {
        const data = await response.json();
        message = data.message;
        error = new RestApiError(message, response.status, data, request.method, request.url);
      }

      setContext('Request', {
        requestId,
        method: request.method,
        url: request.url,
        status: response.status,
        message: message || 'Unknown error',
      });
      captureException(error);

      throw error;
    }
  }
}
